<template>
  <div>
    <!-- STATUS -->
    <div class="panel" v-if="sections.includes('status')">
      <header class="panel__header">
        <div class="profile-compact">
          <div class="logo-container">
            <font-awesome-icon icon="check" class="icon-svg--sm"/>
          </div>
          <div class="profile-compact__info">
            <div class="profile-compact__top">
              <div class="profile-compact__text">
                {{ $t('system.status') }}
                <span class="section-compact__secondary"></span>
              </div>
            </div>
          </div>
        </div>

        <div class="profile-compact__actions">
          <span class="mr-3 btn btn-success cursor-pointer"
                style="top: -8px"
                @click="$emit('complete', activity)"
                data-qid="complete-project"
                v-if="canManage && activity.control_type === 'one time' && activity.type !== 'control_assessment' && activity.type !== 'risk_assessment' && activity.type !== 'bia_assessment' && (activity.status == null || activity.status === 'pending' || activity.status === 'in progress') && activity.progress === 100"
          >
              {{ $t('projects.button.complete_project') }}
          </span>
            <span class="mr-3 btn btn-success cursor-pointer"
                  style="top: -8px"
                  @click="$emit('complete', activity)"
                  data-qid="complete-project"
                  v-if="canManage && activity.control_type === 'one time' && (activity.type === 'control_assessment' || activity.type === 'risk_assessment' || activity.type === 'bia_assessment') && (activity.status == null || activity.status === 'pending' || activity.status === 'in progress') && activity.progress === 100"
            >
            {{ $t('projects.button.complete_project') }}
          </span>
            <span class="mr-3 btn btn-light cursor-pointer"
                  style="top: -8px"
                  @click="$emit('reopen', activity)"
                  data-qid="close-project"
                  v-if="canManage && activity.control_type === 'one time' && (activity.status === 'closed' || activity.status === 'completed')"
            >
            {{ $t('projects.button.reopen_project') }}
         </span>
          <span class="mr-3 btn btn-light cursor-pointer"
                style="top: -8px"
                @click="$emit('close', activity)"
                data-qid="close-project"
                v-if="canManage && activity.control_type === 'one time' && (activity.status == null || activity.status === 'pending' || activity.status === 'in progress')"
          >
            {{ $t('projects.button.close_project') }}
          </span>
        </div>
      </header>

      <div class="panel__body">
        <span v-if="activity.control_type === 'one time'">
          <span v-if="activity.status === 'completed'" class="pr-1">
            <font-awesome-icon icon="share-square" class="risk-1"/>
          </span>
          <span v-if="activity.status === 'completed'">
            {{ $t('projects.button.completed_label') }}
          </span>

          <span v-if="activity.status === 'pending' || activity.status === null" class="pr-1">
            <font-awesome-icon icon="exclamation-triangle" class="risk-1"/>
          </span>
          <span v-if="activity.status === 'pending' || activity.status === null">
            {{ $t('system.pending') }}
          </span>

          <span v-if="activity.status === 'in progress'" class="pr-1">
            <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
          </span>
          <span v-if="activity.status === 'in progress'">
            {{ $t('system.in_progress') }}
          </span>

          <span v-if="activity.status === 'closed'" class="pr-1">
            <font-awesome-icon icon="archive" class="text-muted"/>
          </span>
          <span v-if="activity.status === 'closed'">
            {{ $t('projects.button.closed_label') }}
          </span>
        </span>
        <span v-if="activity.control_type === 'continuous'">
          <span v-if="!activity.is_active_child_project" class="pr-1">
            <font-awesome-icon icon="exclamation-triangle" class="risk-1"/>
            {{ $t('system.pending') }}
          </span>
          <span v-if="activity.is_active_child_project" class="pr-1">
            <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
            {{ $t('system.in_progress') }}
          </span>
        </span>
      </div>
    </div>

    <!-- SCOPE -->
    <div class="panel" v-if="sections.includes('scope')">
      <ControlAssessmentScopeTable
          :object="activity"
          @updated="$emit('updated')"
          :measureTask="measureTask"
      ></ControlAssessmentScopeTable>
    </div>

    <!-- SCHEDULE (engagements: timeline and dates as on the Overview; editing shows the date fields) -->
    <div class="v2-card schedule-v2" :class="{'schedule-v2--editing': editingSchedule}" v-if="v2 && sections.includes('schedule') && activity.control_type === 'one time'" data-qid="planning-schedule">
      <div class="v2-card__head">
        <h3>{{ $t('projects.tab.schedule') }}</h3>
        <span v-if="!editingSchedule" class="v2-tag" :class="activity.overdue_notifications ? 'v2-tag--teal' : 'v2-tag--grey'">
          <font-awesome-icon :icon="activity.overdue_notifications ? 'bell' : 'bell-slash'" class="mr-1"/>{{ activity.overdue_notifications ? $t('projects.engagement.overview.reminders_on') : $t('projects.engagement.overview.reminders_off') }}
        </span>
        <span class="icon-btn"
              data-qid="schedule-s-button"
              v-if="!editingSchedule && canEditSchedule"
              @click="startEditingSchedule"
              v-b-tooltip.hover
              :title="$t('system.edit')"
        ><font-awesome-icon icon="pen"/></span>
      </div>

      <div class="v2-card__body">
        <b-alert v-if="scheduleError.length"
                 show
                 variant="danger"
                 data-qid="schedule-error"
        >{{ scheduleError }}</b-alert>

        <EngagementScheduleCard v-if="!editingSchedule" :project="activity" embedded></EngagementScheduleCard>

        <template v-else>
          <div class="schedule-v2__fields">
            <div v-for="field in dateFields"
                 :key="'edit-' + field.key"
                 class="schedule-v2__field"
                 :data-qid="'schedule-date-' + field.key"
            >
              <label><font-awesome-icon :icon="field.icon" class="mr-1"/>{{ $t(field.label) }}</label>
              <datepicker class="date-picker"
                          calendar-class="left"
                          v-model="scheduleForm[field.key]"
                          :clear-button="true"
                          :placeholder="$t(field.label)"
                          @selected="field.key === 'start_date' && startDateSelected($event)"
              ></datepicker>
            </div>
          </div>
          <label class="schedule-v2__label">{{ $t('projects.create.overdue_reminder') }}</label>
          <div class="task-choices" data-qid="schedule-overdue-reminders">
            <span v-for="option in yesNoOptions"
                  :key="'reminders-' + option.value"
                  class="task-choice task-choice--teal"
                  :class="{'task-choice--on': scheduleForm.overdue_notifications === option.value}"
                  @click="scheduleForm.overdue_notifications = option.value"
            >
              <font-awesome-icon :icon="option.value ? 'bell' : 'bell-slash'"/>{{ option.text }}
            </span>
          </div>
          <div class="v2-field__actions">
            <b-button size="sm"
                      variant="secondary"
                      @click="editingSchedule = false"
                      data-qid="schedule-cancel-button"
            >{{ $t('projects.create.cancel') }}</b-button>
            <b-button size="sm"
                      variant="success"
                      @click="saveSchedule"
                      data-qid="schedule-save-button"
            >{{ $t('tasks.modal.save') }}</b-button>
          </div>
        </template>
      </div>
    </div>

    <div class="panel" v-if="!v2 && sections.includes('schedule') && activity.control_type === 'one time'">
      <header class="panel__header">
        <div class="profile-compact">
          <div class="logo-container">
            <font-awesome-icon icon="bell" class="icon-svg--sm"/>
          </div>
          <div class="profile-compact__info">
            <div class="profile-compact__top">
              <div class="profile-compact__text">
                {{ $t('projects.tab.schedule') }}
                <span class="section-compact__secondary"></span>
              </div>
            </div>
          </div>
        </div>

        <div class="profile-compact__actions">
          <span class="icon-btn"
                data-qid="schedule-s-button"
                v-if="!editingSchedule && canEditSchedule"
                @click="startEditingSchedule"
          >
            <font-awesome-icon icon="pen"/>
          </span>
          <span class="icon-btn btn-delete"
                data-qid="schedule-cancel-button"
                v-if="editingSchedule"
                @click="editingSchedule = false"
          >
            <font-awesome-icon icon="rotate-left"/>
          </span>
          <span class="icon-btn btn-edit"
                data-qid="schedule-save-button"
                v-if="editingSchedule"
                @click="saveSchedule"
          >
            <font-awesome-icon icon="save"/>
          </span>
        </div>
      </header>

      <div class="panel__body">
        <b-alert v-if="scheduleError.length"
                 show
                 variant="danger"
                 data-qid="schedule-error"
        >
          {{ scheduleError }}
        </b-alert>

        <!-- Overdue task reminders -->
        <div class="overdue-reminders" data-qid="overdue-reminders">
          <label class="mb-0">{{ $t('projects.create.overdue_reminder') }}</label>
          <b-form-radio-group v-if="editingSchedule"
                              v-model="scheduleForm.overdue_notifications"
                              :options="yesNoOptions"
                              class="mt-1"
                              data-qid="schedule-overdue-reminders"
          ></b-form-radio-group>
          <div v-else :class="['overdue-reminders__state', activity.overdue_notifications ? 'overdue-reminders__state--on' : 'overdue-reminders__state--off']">
            <font-awesome-icon :icon="activity.overdue_notifications ? 'bell' : 'bell-slash'" class="overdue-reminders__icon"/>
            <span>
              <span class="text-bold">{{ activity.overdue_notifications ? $t('system.yes') : $t('system.no') }}</span>
              <span class="overdue-reminders__hint">
                {{ activity.overdue_notifications ? $t('projects.schedule.overdue_reminders_on_hint') : $t('projects.schedule.overdue_reminders_off_hint') }}
              </span>
            </span>
          </div>
        </div>

        <!-- Project dates, in the order they happen -->
        <div class="schedule-dates" data-qid="schedule-dates">
          <div v-for="field in dateFields"
               :key="field.key"
               class="schedule-dates__item"
               :data-qid="'schedule-date-' + field.key"
          >
            <div class="schedule-dates__label">
              <font-awesome-icon :icon="field.icon" class="mr-1"/>{{ $t(field.label) }}
            </div>
            <datepicker v-if="editingSchedule"
                        class="date-picker"
                        calendar-class="left"
                        v-model="scheduleForm[field.key]"
                        :clear-button="true"
                        :placeholder="$t(field.label)"
                        @selected="field.key === 'start_date' && startDateSelected($event)"
            ></datepicker>
            <template v-else-if="activity[field.key]">
              <div class="schedule-dates__date">{{ activity[field.key] | formatDate('DD MMM YYYY', true) }}</div>
              <div class="schedule-dates__relative" :class="relativeClass(field, activity[field.key])">{{ relativeDate(activity[field.key]) }}</div>
            </template>
            <div v-else class="schedule-dates__date text-muted">{{ $t('system.missing_input') }}</div>
          </div>
        </div>

        <label class="mt-2">{{ $t('projects.schedule.schedule_gantt_label') }}</label>
        <div class="" v-if="scheduleGanttTasks.length">
          <gantt-elastic-wrapper
            ref="scheduleGantt"
            :tasks="scheduleGanttTasks"
            :options="scheduleGanttOptions"
            :dynamic-style="scheduleGanttStyle"
          ></gantt-elastic-wrapper>
        </div>
        <div class="section-container" v-else>
          <span class="text-muted">
            {{ $t('system.missing_input') }}
          </span>
        </div>
      </div>
    </div>

    <!-- SETTINGS -->
    <div class="panel" v-if="sections.includes('settings')">
      <header class="panel__header">
        <div class="profile-compact">
          <div class="logo-container">
            <font-awesome-icon icon="cog" class="icon-svg--sm"/>
          </div>
          <div class="profile-compact__info">
            <div class="profile-compact__top">
              <div class="profile-compact__text">
                {{ $t('dashboard.settings') }}
                <span class="section-compact__secondary"></span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div class="panel__body">
        <div>
          <span>{{ $t('table.last_updated') }}</span>
          <span class="ml-2 text-muted small" data-qid="view-s-modified-date">
            {{ activity.updated_at | formatDate }}
          </span>
          <Avatar v-if="activity.updated_by"
                  :object="activity.updated_by"
                  size="xs"
                  class="ml-3 mr-2"
          ></Avatar>
          <span v-if="activity.updated_by" data-qid="view-s-modified-by">
            {{ activity.updated_by.name }}
          </span>
        </div>

        <label v-if="activity && activity.type !== 'control_assessment'" class="mt-2">
          {{ $t('table.type') }}
        </label>

        <div v-if="activity.type !== 'control_assessment' && activity.type"
             class="mr-1 section-container"
             data-qid="activity-type-field"
        >
          <span class="badge">
            <span v-if="activity.type === 'update'">
              {{ $t('projects.view.update_label') }}
            </span>
            <span v-if="activity.type === 'validation'">
              {{ $t('projects.view.validation_label') }}
            </span>
            <span v-if="activity.type === 'control'">
              {{ $t('projects.view.control_label') }}
            </span>
            <span v-if="activity.type === 'security_measure'">
              {{ $t('projects.view.security_measure_label') }}
            </span>
            <span v-if="activity.type === 'risk_assessment' && activity.assessment">
              {{ $t('maintenance.assessments.type.risk_assessment') }}
            </span>
            <span v-if="activity.type === 'bia_assessment' && activity.assessment">
              {{ $t('maintenance.assessments.type.bia_assessment') }}
            </span>
          </span>
        </div>

        <label v-if="activity.scope" class="mt-2">
          {{ $t('table.scope') }}
        </label>
        <div  v-if="activity.scope" class="mr-1 section-container" data-qid="activity-scope-list">
          <span class="badge" v-if="activity.scope === 'policy'">
            <font-awesome-icon icon="file-invoice"/>
            {{ $t('projects.scope.policy') }}
          </span>
          <span class="badge" v-if="activity.scope === 'system'">
            <font-awesome-icon icon="crosshairs"/>
            {{ $t('projects.system.label') }}
          </span>
          <span class="badge" v-if="activity.scope === 'process'">
            <font-awesome-icon icon="crosshairs"/>
            {{ $t('projects.process.label') }}
          </span>
          <span class="badge" v-if="activity.scope === 'activity'">
            <font-awesome-icon icon="crosshairs"/>
            {{ $t('projects.activity.label') }}
          </span>
          <span class="badge" v-if="activity.scope === 'third_party'">
            <font-awesome-icon icon="crosshairs"/>
            {{ $t('projects.third_party.label') }}
          </span>
        </div>

        <label v-if="activity.assessment && (activity.type === 'control_assessment' || activity.type === 'risk_assessment' || activity.type === 'bia_assessment')" class="mt-2">
          {{ $t('table.template') }}
        </label>
        <div class="mr-1 section-container" data-qid="activity-tags-list" v-if="activity.assessment && (activity.type === 'control_assessment' || activity.type === 'risk_assessment' || activity.type === 'bia_assessment')">
          <span class="badge">
              {{ activity.assessment.name }}
          </span>
        </div>

        <label v-if="activity.control_type" class="mt-2">
          {{ $t('projects.create.schedule_label') }}
        </label>
        <div class="mr-1 section-container" data-qid="activity-tags-list" v-if="activity.control_type === 'continuous'">
          <span class="badge">
            {{ $t('projects.schedule.continuous') }}
          </span>
        </div>
        <div class="mr-1 section-container" data-qid="activity-tags-list" v-if="!activity.project && activity.control_type === 'one time'">
          <span class="badge">
              {{ $t('projects.schedule.one_time') }}
          </span>
        </div>
        <div class="mr-1 section-container" data-qid="activity-tags-list" v-if="activity.project && activity.control_type === 'one time'">
          <span class="badge">
            <span>{{ $t('projects.schedule.' + $options.filters.slugify(activity.schedule_type)) }}</span>
          </span>
        </div>

        <label class="mt-2" v-if="activity.additional_client && activity.additional_client.slug">
          {{ $t('projects.create.external_link_label') }}
        </label>
        <div class="mr-1 section-container" v-if="activity.additional_client && activity.additional_client.slug">
          <span class="badge" v-if="getClient.slug !== activity.additional_client.slug">
            <Avatar :object="activity.additional_client" size="xs"></Avatar>
            {{ activity.additional_client.name }}
          </span>
          <span class="badge" v-if="getClient.slug === activity.additional_client.slug">
            <Avatar :object="activity.active_client" size="xs"></Avatar>
            {{ activity.active_client.name }}
          </span>
        </div>

        <label v-if="activity.tags_translated && activity.tags_translated.length" class="mt-2">
          {{ $t('table.tags') }}
        </label>
        <div v-if="activity.tags_translated && activity.tags_translated.length" data-qid="tags-list" class="section-container">
          <span class="badge"
                v-for="(tag, index) in getReducedTags(activity.tags_translated)"
                :key="index"
                :title="tag.name_translated"
          >
            <font-awesome-icon icon="tag"/>
            {{ tag.name_translated | truncate(35) }}
          </span>
          <span v-if="activity.tags_translated.length > 3"
                class="badge cursor-pointer"
                @click="$emit('open-tags', activity)"
          >
            <font-awesome-icon icon="tags"/>
            + {{ activity.tags_translated.length - 3 }}
          </span>
        </div>

        <label class="mt-2">
          {{ $t('projects.create.reporting_language') }}
        </label>
        <div  v-if="activity.language" class="mr-1 section-container" data-qid="activity-language-field">
          {{ selectedLanguage.text }}
        </div>
        <div v-else class="section-container">
          <span class="text-muted">
            {{ $t('system.missing_input') }}
          </span>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Datepicker from 'vuejs-datepicker';
import Avatar from '@/components/Avatar.vue';
import ControlAssessmentScopeTable from '@/views/Projects/ControlAssessmentScopeTable.vue';
import GanttElasticWrapper from "@/components/GanttElasticWrapper.vue";
import EngagementScheduleCard from '@/views/Projects/Engagement/EngagementScheduleCard.vue';

// Scope, schedule and settings panels of a project, shown on Basic info or (engagements) on the Settings tab
export default {
  name: 'ProjectSettingsPanels',
  props: {
    activity: {
      type: Object,
      required: true
    },
    sections: {
      type: Array,
      default: () => ['status', 'scope', 'schedule', 'settings']
    },
    measureTask: {},
    selectedLanguage: {},
    scheduleGanttTasks: {
      type: Array,
      default: () => []
    },
    scheduleGanttOptions: {},
    scheduleGanttStyle: {},
    // Engagements: the schedule in the V2 style, without the gantt chart
    v2: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Datepicker,
    Avatar,
    ControlAssessmentScopeTable,
    GanttElasticWrapper,
    EngagementScheduleCard
  },
  data: function() {
    return {
      editingSchedule: false,
      scheduleError: '',
      scheduleForm: {},
      yesNoOptions: [
        { text: this.$t('system.yes'), value: 1 },
        { text: this.$t('system.no'), value: 0 }
      ]
    }
  },
  computed: {
    canEditSchedule() {
      return this.isGranted([this.permissions.PROJECT_EDIT, this.permissions.PROJECT_EDIT_OWN], this.activity) && this.canManage && this.activity.status !== 'closed' && this.activity.status !== 'completed';
    },
    dateFields() {
      let fields = [{ key: 'start_date', label: 'projects.schedule.start_date_label', icon: 'play' }];
      if (this.activity.type === 'control_assessment') {
        fields.push({ key: 'data_collection_end_date', label: 'projects.schedule.data_due_date_label', icon: 'folder-open', due: true });
      }
      fields.push({ key: 'end_date', label: 'projects.schedule.due_date_label', icon: 'calendar-alt', due: true });
      return fields;
    },
    getClient() {
      return this.$store.state.client
    },
    canManage() {
      // On engagements only Managers change the schedule
      return !this.activity.engagement_permissions || !!this.activity.engagement_permissions.manage;
    }
  },
  methods: {
    startEditingSchedule() {
      this.scheduleError = '';
      this.scheduleForm = {
        overdue_notifications: this.activity.overdue_notifications ? 1 : 0,
        start_date: this.localDate(this.activity.start_date),
        end_date: this.localDate(this.activity.end_date),
        data_collection_end_date: this.localDate(this.activity.data_collection_end_date)
      };
      this.editingSchedule = true;
    },
    // Due dates before a new start date no longer make sense
    startDateSelected(date) {
      _.forEach(['end_date', 'data_collection_end_date'], key => {
        if (date && this.scheduleForm[key] && this.dateValue(this.scheduleForm[key]) < this.dateValue(date)) {
          this.scheduleForm[key] = '';
        }
      });
    },
    // The calendar day of an API date as a local date, so the datepicker shows the same day in every time zone
    localDate(value) {
      if (!value) {
        return '';
      }
      const parts = String(value).substring(0, 10).split('-');
      return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    },
    // YYYY-MM-DD in local time, for dates from the datepicker and from the API
    dateValue(value) {
      if (!value) {
        return '';
      }
      return value instanceof Date ? value.toLocaleDateString('en-CA') : String(value).substring(0, 10);
    },
    daysFromToday(value) {
      const moment = require('moment');
      return moment(this.dateValue(value), 'YYYY-MM-DD').diff(moment().startOf('day'), 'days');
    },
    relativeDate(value) {
      const days = this.daysFromToday(value);
      if (days === 0) {
        return this.$t('projects.schedule.today');
      }
      return days > 0 ? this.$tc('projects.schedule.in_days', days, { count: days }) : this.$tc('projects.schedule.days_ago', -days, { count: -days });
    },
    relativeClass(field, value) {
      const days = this.daysFromToday(value);
      if (!field.due || this.activity.status === 'completed' || this.activity.status === 'closed') {
        return '';
      }
      if (days < 0) {
        return 'schedule-dates__relative--overdue';
      }
      return days <= 3 ? 'schedule-dates__relative--soon' : '';
    },
    saveSchedule() {
      let self = this;
      const form = {
        start_date: this.dateValue(this.scheduleForm.start_date),
        end_date: this.dateValue(this.scheduleForm.end_date),
        data_collection_end_date: this.dateValue(this.scheduleForm.data_collection_end_date)
      };

      let errors = [];
      _.forEach(this.dateFields, field => {
        if (!form[field.key]) {
          errors.push(this.$t({
            start_date: 'projects.schedule.start_date_error',
            end_date: 'projects.schedule.end_date_error',
            data_collection_end_date: 'projects.schedule.data_end_date_error'
          }[field.key]));
        }
      });
      if (!errors.length && form.start_date > form.end_date) {
        errors.push(this.$t('projects.schedule.start_date_greater_error'));
      }
      if (errors.length) {
        this.scheduleError = errors.join(' ');
        return;
      }

      // Only changed dates are sent: saving a start date also starts the project and notifies the assignees
      let payload = { overdue_notifications: this.scheduleForm.overdue_notifications };
      _.forEach(this.dateFields, field => {
        if (form[field.key] !== this.dateValue(this.activity[field.key])) {
          payload[field.key] = form[field.key];
        }
      });

      piincHttp.put('projects/' + this.activity.slug, payload).then(function() {
        self.editingSchedule = false;
        self.scheduleError = '';
        self.$toastr('success', self.$t('projects.update.success'));
        self.$emit('updated');
      }, function(error) {
        self.scheduleError = error.data && error.data.message ? error.data.message : '';
      });
    },
    getReducedTags(tags) {
      return tags.length > 3 ? _.cloneDeep(tags).slice(0, 3) : tags;
    }
  }
}
</script>
